/**
 * Noornote - High-Performance Nostr Web Client
 * Main application entry point
 */

// Load SVG sprite sheet — inject into DOM before any components render
import spriteContent from '/icons.svg?raw';
const spriteDiv = document.createElement('div');
spriteDiv.innerHTML = spriteContent;
document.body.insertBefore(
  spriteDiv.firstElementChild!,
  document.body.firstChild
);

// Capture ?scc= secondary-pane state immediately (before the ?r= branch below can
// strip the query). Restored after login in App.ts; otherwise left in the URL.
const __capturedSccParam = new URLSearchParams(window.location.search).get(
  'scc'
);
if (__capturedSccParam) {
  window.__noornote_scc_param = __capturedSccParam;
}

// Capture ?r= relay browser parameter immediately (before any module side-effects or HMR)
const __capturedRelayParam = new URLSearchParams(window.location.search).get(
  'r'
);
if (__capturedRelayParam) {
  window.history.replaceState({}, '', window.location.pathname);
  window.__noornote_relay_param = __capturedRelayParam;
}

// Suppress NDK v3 serialization errors for malformed events from other clients
window.addEventListener('unhandledrejection', event => {
  if (
    String(event.reason).includes(
      "Can't serialize event with invalid properties"
    )
  ) {
    event.preventDefault();
  }
});

import { App } from './App';
import { AuthService } from './services/AuthService';
import { UserProfileService } from './services/UserProfileService';
import { SystemLogger } from './services/SystemLogger';
import { CrashLogger } from './services/CrashLogger';
import './styles/main.scss';
import './services/AuthStateManager'; // Initialize AuthStateManager and window.isLoggedIn()
// MutualChangeService: only load if Extended Follows addon is enabled
import { isExtendedFollowsEnabled } from './addons/extended-follows/index';
if (isExtendedFollowsEnabled()) {
  void import('./services/MutualChangeService');
}
import './services/AppBadgeService'; // Initialize AppBadgeService (browser tab + dock badge)

// Track failed image loads (URL → log message for removal)
const failedImageLogs = new Map<string, string>();

// Global error handler for resource loading failures (images, videos, etc.)
// Downgrades console.error to console.warn for non-critical resource failures
window.addEventListener(
  'error',
  event => {
    // Only handle resource loading errors (404, CORS, etc.)
    const target = event.target as HTMLElement;
    if (
      target instanceof HTMLImageElement ||
      target instanceof HTMLVideoElement
    ) {
      // Prevent default error logging (red in console)
      event.preventDefault();

      const src =
        target instanceof HTMLImageElement
          ? target.src
          : (target as HTMLVideoElement).src;
      const systemLogger = SystemLogger.getInstance();

      // Log to "ImageLoader" category (falls under "Local" in SystemLogger)
      const message = `Failed to load ${target.tagName.toLowerCase()}: ${src} (Resource unavailable)`;
      systemLogger.warn('ImageLoader', message);

      // Track this failure so we can remove it on successful retry.
      // Cap: a long session on spammy feeds can hit hundreds of broken
      // hosts — drop the oldest entries so the map never grows unbounded.
      if (failedImageLogs.size >= 500) {
        const oldestKey = failedImageLogs.keys().next().value;
        if (oldestKey !== undefined) failedImageLogs.delete(oldestKey);
      }
      failedImageLogs.set(src, message);

      // Setup retry listener: if image loads successfully later, remove the error log
      if (target instanceof HTMLImageElement) {
        const onLoad = () => {
          if (failedImageLogs.has(src)) {
            // Remove the error message from System Log (extra points!)
            systemLogger.removeLog('ImageLoader', message);
            failedImageLogs.delete(src);
          }
          target.removeEventListener('load', onLoad);
        };
        target.addEventListener('load', onLoad);
      }
    }
  },
  true
); // Use capture phase to intercept before default handler

// Patch HTMLImageElement.src setter to prevent empty/null/undefined URLs
// This prevents localhost:3000 errors when profile pictures are missing
const originalSrcDescriptor = Object.getOwnPropertyDescriptor(
  HTMLImageElement.prototype,
  'src'
);
if (originalSrcDescriptor && originalSrcDescriptor.set) {
  Object.defineProperty(HTMLImageElement.prototype, 'src', {
    set(value: string) {
      // Only set src if value is truthy and not 'null' string
      if (value && value !== 'null' && value !== 'undefined') {
        originalSrcDescriptor.set!.call(this, value);
      }
      // Silently ignore empty/null/undefined values
    },
    ...(originalSrcDescriptor.get && {
      get(this: HTMLImageElement): string {
        // lib.dom types the getter's return as any — normalize to string
        return String(originalSrcDescriptor.get!.call(this));
      },
    }),
    configurable: true,
  });
}

// Application initialization
async function init(): Promise<void> {
  try {
    // Initialize crash logging FIRST (before anything else can fail)
    await CrashLogger.init();

    // Initialize the main application
    const app = new App();
    await app.initialize();

    // Remove loading screen
    const loadingElement = document.getElementById('loading');
    if (loadingElement) {
      loadingElement.remove();
    }

    // Log ready message with username
    const systemLogger = SystemLogger.getInstance();
    const authService = AuthService.getInstance();
    const currentUser = authService.getCurrentUser();

    if (currentUser) {
      const profileService = UserProfileService.getInstance();
      const username = profileService.getUsername(currentUser.pubkey);
      systemLogger.info('Main', `NoorNote ready for user: ${username}`);
    } else {
      systemLogger.info('Main', 'NoorNote ready');
    }
  } catch (error) {
    console.error('Failed to initialize Noornote:', error);

    // Show error message to user
    const appElement = document.getElementById('app');
    if (appElement) {
      appElement.innerHTML = `
        <div class="error-screen">
          <h2>Failed to load Noornote</h2>
          <p>Please refresh the page to try again.</p>
          <button onclick="window.location.reload()">Refresh</button>
        </div>
      `;
    }
  }
}

// Start the application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  void init();
}
